import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, combineLatest, catchError, of } from 'rxjs';

import {
  AdmissionsService,
  Student,
  UIConfiguration,
  PlaceOutboundCallResponse,
  PlaceOutbountCallRequest,
  UserService,
  DispositionService,
  PropertyValue
} from '../../lib';
import { AdmissionUIMessengerService } from './admission-ui-messenger.service';

@Injectable({
  providedIn: 'root'
})
export class AdmissionUICommonDataService {

  private readonly multiStudentDataSource = new BehaviorSubject<Student[]>([]);
  multiStudentData = this.multiStudentDataSource.asObservable();

  private readonly isStitchInSource = new BehaviorSubject<boolean>(false);
  isStitchIn = this.isStitchInSource.asObservable();

  private readonly selectedDispositionOptionSubject = new BehaviorSubject<any>(null);
  selectedDispositionOption$ = this.selectedDispositionOptionSubject.asObservable();

  private readonly previousUserStatusSource = new BehaviorSubject<string>('');
  previousUserStatus = this.previousUserStatusSource.asObservable();
  private modalValidHosts$?: Observable<string[]>; // Add this line


  uiConfigurationRetrieved: boolean = false;
  uiConfiguration: UIConfiguration[] = [];
  DispWindowShowForSchoolStatuses: string = '';
  StitchWindowShowForSchoolStatuses: string = '';
  accountIdRetrieved: boolean = false;
  accountId: number | undefined;
  staffId: number | undefined;
  public localTouch: boolean = true;
  public terminatedStatus: string[] = ['terminated', 'disconnected', 'transferred'];
  public connectedStatus: string[] = ['connected', 'dialing', 'contacting'];
  public alertingStatus: string[] = ['alerting', 'waiting', 'presenting', 'Not Set', 'None'];
  public contactedEngaged?: PropertyValue;
  public contactedNotEngaged?: PropertyValue;
  public doNotCall?: PropertyValue;
  public stitchInCall?: PropertyValue;

  constructor(
    private readonly admissionsService: AdmissionsService,
    private readonly userService: UserService,
    private readonly dispositionService: DispositionService,
    private readonly messenger: AdmissionUIMessengerService
  ) {
    // Load property values during service initialization
    this.dispositionService.apiDispositionPropertyValueGet().subscribe(data => {
      // Pre-find common property values for quick access
      this.contactedEngaged = data.find(obj => obj.value === 'Contacted - Engaged');
      this.contactedNotEngaged = data.find(obj => obj.value === 'Contacted - Not Engaged');
      this.doNotCall = data.find(obj => obj.value === 'Mark as Do Not Call');
      this.stitchInCall = data.find(obj => obj.value === 'Stitch-In Call');
    });

    this.messenger.setLocalTouch$.subscribe((localTouch: boolean) => {
      this.localTouch = localTouch;
    });
  }


  setMultiStudentData(studentData: Student[]): void {
    this.multiStudentDataSource.next(studentData);
  }

  setIsStitchIn(isStitchIn: boolean): void {
    this.isStitchInSource.next(isStitchIn);
  }

  setSelectedDispositionOption(option: any) {
    this.selectedDispositionOptionSubject.next(option);
  }

  setPreviousUserStatus(previousUserStatus: string) {
    this.previousUserStatusSource.next(previousUserStatus);
  }

  private getUIConfiguration(): Observable<UIConfiguration[]> {
    return new Observable<UIConfiguration[]>(obs => {
      if (!this.uiConfigurationRetrieved) {
        this.admissionsService.apiGetUIConfigurationGet().subscribe(data => {
          this.uiConfiguration = data;
          this.uiConfigurationRetrieved = true;
          obs.next(this.uiConfiguration);
        });
      } else {
        obs.next(this.uiConfiguration);
      }
    });
  }

  getUIConfigurationAndSchoolStatuses(): Observable<{ uiConfiguration: UIConfiguration[], DispWindowShowForSchoolStatuses: string, StitchWindowShowForSchoolStatuses: string }> {
    return new Observable<{ uiConfiguration: UIConfiguration[], DispWindowShowForSchoolStatuses: string, StitchWindowShowForSchoolStatuses: string }>(obs => {
      this.getUIConfiguration().subscribe(uiConfiguration => {
        this.retrieveDispWindowShowForSchoolStatuses();
        this.retrieveStitchWindowShowForSchoolStatuses();
        this.retrieveModalValidHosts();
        obs.next({ uiConfiguration: uiConfiguration, DispWindowShowForSchoolStatuses: this.DispWindowShowForSchoolStatuses, StitchWindowShowForSchoolStatuses: this.StitchWindowShowForSchoolStatuses });
      });
    });
  }

  retrieveModalValidHosts(): Observable<string[]> {
    if (!this.modalValidHosts$) {
      this.modalValidHosts$ = new Observable<string[]>(obs => {
        this.getUIConfiguration().subscribe(uiConfiguration => {
          const validHosts = uiConfiguration?.find(c => c.key === 'Admissions.LeadUIValidHosts')?.value?.split(',') || [];
          const normalizedValidHosts = validHosts.map(vh => this.normalizeHost(vh));
          obs.next(normalizedValidHosts);
          obs.complete();
        });
      });
    }
    return this.modalValidHosts$;
  }

  normalizeHost(host: string): string {
    if (host) {
      return host.replace(/^(https?:\/\/)?/i, '');
    }
    return host;
  }


  private retrieveDispWindowShowForSchoolStatuses(): void {
    const statuses = this.uiConfiguration?.find(c => c.key === 'DispositionWindow.ShowForSchoolStatuses')?.value;
    if (statuses) {
      this.DispWindowShowForSchoolStatuses = statuses;
    }
  }

  private retrieveStitchWindowShowForSchoolStatuses(): void {
    const statuses = this.uiConfiguration?.find(c => c.key === 'StitchInWindow.ShowForSchoolStatuses')?.value;
    if (statuses) {
      this.StitchWindowShowForSchoolStatuses = statuses;
    }
  }

  getAccountAndStaffId(): Observable<{ accountId: number, staffId: number }> {
    return new Observable<{ accountId: number, staffId: number }>(obs => {
      if (!this.accountIdRetrieved) {
        this.accountIdRetrieved = true;
        const apiAccountGet = this.userService.apiAccountGet().pipe(catchError(e => of({ id: -1 })));
        const apiStaffGet = this.userService.apiSyStaffGet().pipe(catchError(e => of({ id: -1 })));

        combineLatest([apiAccountGet, apiStaffGet]).subscribe(([apiAccountGet, apiStaffGet]) => {
          this.staffId = apiStaffGet.id;
          this.accountId = apiAccountGet.id;

          if (this.accountId !== undefined && this.staffId !== undefined) {
            obs.next({ accountId: this.accountId, staffId: this.staffId });
          } else {
            obs.error('Account ID or Staff ID is undefined');
          }
        });
      } else if (this.accountId !== undefined && this.staffId !== undefined) {
        obs.next({ accountId: this.accountId, staffId: this.staffId });
      } else {
        obs.error('Account ID or Staff ID is undefined');
      }

    });
  }

  dial(studentId: number, phoneNumber: string, inquiryId: number, dialerContactId: number, accountId: number,
    emailConversationId: string | null = null, participantId: string | null = null, isLocalTouch: boolean | null = null,
    interactionType: string | null = null
  ): Observable<PlaceOutboundCallResponse> {
    // Convert string to enum with fallback logic
    const telecomInteractionType = this.convertToTelecomInteractionTypeEnum(interactionType);
    const placeOutboundCallRequest = {
      syStudentId: studentId,
      phoneNumber: phoneNumber,
      isLocalTouch: isLocalTouch ?? this.localTouch,
      syStudentInquiryId: inquiryId,
      dialerContactId: dialerContactId,
      accountId: accountId,
      telecomInteractionType: telecomInteractionType,
      participantId: participantId,
      conversationId: emailConversationId
    }

    return this.admissionsService.apiAdmissionOutboundCallPlaceCallPost(placeOutboundCallRequest);
  }

  toggleUserStatus(userStatus: string): Observable<any> {
    return this.userService.apiGenesysSetGenesysUserStatusPatch({ status: userStatus || 'Available' });
  }

  private convertToTelecomInteractionTypeEnum(interactionType: string | null): PlaceOutbountCallRequest.TelecomInteractionTypeEnum {
    // Default value
    const defaultValue = PlaceOutbountCallRequest.TelecomInteractionTypeEnum.NUMBER_null4; // 'OutgoingCall'

    // If null, empty, or trimmed empty - return default
    if (!interactionType || !interactionType.trim()) {
      return defaultValue;
    }

    try {
      const inputString = interactionType.trim().toLowerCase();

      // Check exact match against the 4 available call types (case-insensitive)
      switch (inputString) {
        case 'callback':
          return PlaceOutbountCallRequest.TelecomInteractionTypeEnum.NUMBER_null; // 'Callback'
        case 'dialercall':
          return PlaceOutbountCallRequest.TelecomInteractionTypeEnum.NUMBER_null2; // 'DialerCall'
        case 'incomingcall':
          return PlaceOutbountCallRequest.TelecomInteractionTypeEnum.NUMBER_null3; // 'IncomingCall'
        case 'outgoingcall':
          return PlaceOutbountCallRequest.TelecomInteractionTypeEnum.NUMBER_null4; // 'OutgoingCall'
        default:
          // No match found - log available values and return default
          console.warn(`LeadUI-Input value "${interactionType}" does not match any enum values. Available values: [Callback, DialerCall, IncomingCall, OutgoingCall]. Using default value: OutgoingCall`);
          return defaultValue;
      }

    } catch (error) {
      console.error(`LeadUI-Error converting interaction type "${interactionType}":`, error);
      return defaultValue;
    }
  }
}
